import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./bookings.styles";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";

const CancelAppointmentModal = ({ open, onClose, onConfirm, booking }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const cancellationReasons = [
    { value: "client-request", label: "Client Request" },
    { value: "consultant-unavailable", label: "Consultant Unavailable" },
    { value: "scheduling-conflict", label: "Scheduling Conflict" },
    { value: "medical-emergency", label: "Medical Emergency" },
    { value: "weather-conditions", label: "Weather Conditions" },
    { value: "other", label: "Other" },
  ];

  const handleConfirm = () => {
    onConfirm?.({
      _id: booking?._id,
      reason,
      notes,
    });
    setReason("");
    setNotes("");
    onClose();
  };

  const handleClose = () => {
    setReason("");
    setNotes("");
    onClose();
  };

  const sectionLabelSx = {
    fontSize: 10,
    fontWeight: 700,
    color: colors.textMuted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    mb: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.16)",
            maxWidth: 480,
            overflow: "visible",
          },
        },
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: "center" }}>
        {/* Warning Icon */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2.5,
            bgcolor: colors.dangerLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            mb: 3,
          }}
        >
          <EventBusyOutlinedIcon sx={{ fontSize: 28, color: colors.danger }} />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: colors.textPrimary,
            mb: 1.5,
          }}
        >
          Cancel Appointment?
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textSecondary,
            lineHeight: 1.7,
            mb: 3,
            maxWidth: 320,
            mx: "auto",
          }}
        >
          This action will notify both the client and the consultant. Please
          provide a reason for this cancellation.
        </Typography>

        {/* Booking Details */}
        <Box
          sx={{
            bgcolor: colors.bgPrimary,
            borderRadius: 2.5,
            p: 2.5,
            mb: 3,
            textAlign: "left",
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                BOOKING ID
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                #{booking?._id?.slice(-8).toUpperCase() || "BOOKING"}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                CLIENT
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                {booking?.clientName || "N/A"}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: colors.textMuted,
                  letterSpacing: 0.5,
                }}
              >
                SCHEDULED FOR
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                {booking?.date && booking?.slot
                  ? `${dayjs(booking.date).format("MMM DD, YYYY")} • ${booking.slot}`
                  : "N/A"}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Reason for Cancellation */}
        <Box sx={{ textAlign: "left", mb: 3 }}>
          <Typography sx={sectionLabelSx}>REASON FOR CANCELLATION</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                borderRadius: 2.5,
                fontSize: 14,
                bgcolor: "#fff",
                height: 48,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              }}
            >
              <MenuItem value="">
                <Typography sx={{ fontSize: 14, color: colors.textMuted }}>
                  Select a reason
                </Typography>
              </MenuItem>
              {cancellationReasons.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  <Typography sx={{ fontSize: 14, color: colors.textPrimary }}>
                    {r.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Additional Notes */}
        <Box sx={{ textAlign: "left" }}>
          <Typography sx={sectionLabelSx}>ADDITIONAL NOTES</Typography>
          <TextField
            multiline
            rows={2}
            fullWidth
            placeholder="Provide more context for the cancellation..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                fontSize: 14,
                bgcolor: "#fff",
                "& fieldset": {
                  borderColor: colors.border,
                },
                "&:hover fieldset": {
                  borderColor: primaryColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: primaryColor,
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          pb: 4,
          px: 4,
          flexWrap: "nowrap",
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            minWidth: 130,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            whiteSpace: "nowrap",
            color: colors.textSecondary,
            borderColor: colors.border,
            py: 1.25,
            px: 3,
            borderRadius: 2,
            "&:hover": {
              borderColor: colors.textSecondary,
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          Keep Booking
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!reason}
          sx={{
            minWidth: 180,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            whiteSpace: "nowrap",
            bgcolor: colors.danger,
            color: "#FFF",
            py: 1.25,
            px: 3,
            borderRadius: 2,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#B91C1C",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              bgcolor: colors.border,
              color: colors.textMuted,
            },
          }}
        >
          Confirm Cancellation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelAppointmentModal;
